/**
 * Reduce a photograph in the browser before it is uploaded.
 *
 * **This is a bandwidth optimisation, never a security boundary.** The server decodes, strips and
 * re-encodes every image it accepts regardless of what happened here — §6 of the domain model is
 * explicit that a client-side strip is not sufficient, because an old client predates it and a
 * hostile one skips it, and neither announces itself. Nothing downstream may assume this ran.
 *
 * What it genuinely buys is threefold. A phone photograph is 3–8 MB and the stored copy is a few
 * hundred kilobytes, so a parent on a campsite's worth of signal uploads the small one. The canvas
 * re-encode converts **HEIC to JPEG**, which is what iPhones actually produce and what the
 * server-side decoder does not read. And it keeps the API's single replica from holding a long
 * request open while a large body arrives.
 *
 * The cost is `capturedAt`: drawing to a canvas discards the EXIF along with everything else, so
 * the date has to be read off the original *before* the re-encode and sent alongside the file. That
 * makes it a claim rather than evidence — fine for a date under a gallery photo, and deliberately
 * not the basis for anything that matters.
 */

/**
 * The long edge the client aims for. Matches `StoredFile.MaximumDimension` on the server, which
 * will reduce anything larger anyway — the point of matching is that the common path arrives
 * already correct and the server's resize becomes a no-op.
 */
const MAX_DIMENSION = 2560;

/** Visually indistinguishable from the original on a phone, at roughly a tenth the bytes. */
const JPEG_QUALITY = 0.82;

export interface DownsizedImage {
  /** The re-encoded image, ready to be sent as the `file` part of the multipart body. */
  blob: Blob;
  /** ISO-8601, or null when the original carried no readable capture time. */
  capturedAt: string | null;
  /** What the browser produced. Always one of the types the server accepts. */
  contentType: string;
}

/**
 * Downsizes an image file, returning the bytes to upload and whatever capture time the original
 * carried.
 *
 * Falls back to the original file whenever anything goes wrong — a format the browser will not
 * decode, a canvas that refuses to export, a `File` that is not an image at all. The server is the
 * authority on what is acceptable, so failing *open* here is right: it hands the question to the
 * component that can actually answer it rather than refusing an upload the API would have taken.
 */
export async function downsizeImage(file: File): Promise<DownsizedImage> {
  const capturedAt = await readCapturedAt(file);

  try {
    const bitmap = await createImageBitmap(file);

    try {
      const { width, height } = fit(bitmap.width, bitmap.height, MAX_DIMENSION);

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const context = canvas.getContext('2d');

      if (!context) {
        return { blob: file, capturedAt, contentType: file.type };
      }

      // A PNG with transparency would go black on a JPEG background, so keep PNG as PNG. Everything
      // else — JPEG, HEIC, whatever the camera produced — becomes JPEG.
      const keepPng = file.type === 'image/png';
      const contentType = keepPng ? 'image/png' : 'image/jpeg';

      if (!keepPng) {
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, width, height);
      }

      context.drawImage(bitmap, 0, 0, width, height);

      const blob = await toBlob(canvas, contentType, JPEG_QUALITY);

      // A re-encode is not guaranteed to be smaller — a small photo of noise can grow. Send
      // whichever is actually less.
      return blob && blob.size < file.size
        ? { blob, capturedAt, contentType }
        : { blob: file, capturedAt, contentType: file.type };
    } finally {
      bitmap.close();
    }
  } catch {
    return { blob: file, capturedAt, contentType: file.type };
  }
}

function fit(width: number, height: number, longEdge: number): { width: number; height: number } {
  if (width <= longEdge && height <= longEdge) {
    return { width, height };
  }

  const scale = longEdge / Math.max(width, height);

  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

/**
 * Reads EXIF `DateTimeOriginal` out of a JPEG, by hand.
 *
 * A whole EXIF library would be a dependency for one tag. This walks the JPEG segment list to
 * APP1, then the TIFF IFD inside it, and reads the two tags that matter — deliberately narrow, and
 * it returns null rather than throwing on anything it does not recognise, because a photo with no
 * date is a much smaller problem than an upload that fails.
 *
 * Only the first 128 KB is read: the EXIF block sits at the front of the file, and slicing keeps a
 * 12 MB photograph from being pulled into memory to find a timestamp.
 */
export async function readCapturedAt(file: File): Promise<string | null> {
  if (file.type !== 'image/jpeg' && !file.name.toLowerCase().endsWith('.jpg')) {
    // HEIC carries its capture time somewhere else entirely, and the browser will not hand it over.
    // Null is the honest answer.
    return null;
  }

  try {
    const head = await file.slice(0, 128 * 1024).arrayBuffer();
    const view = new DataView(head);

    if (view.byteLength < 4 || view.getUint16(0) !== 0xffd8) {
      return null; // Not a JPEG.
    }

    let offset = 2;

    while (offset + 4 < view.byteLength) {
      if (view.getUint8(offset) !== 0xff) {
        return null; // Lost the segment structure; do not guess.
      }

      const marker = view.getUint8(offset + 1);
      const length = view.getUint16(offset + 2);

      // APP1, and it must actually say "Exif" — APP1 is also where XMP lives.
      if (marker === 0xe1 && offset + 10 < view.byteLength && readAscii(view, offset + 4, 4) === 'Exif') {
        return readExifDate(view, offset + 10);
      }

      // Start of scan: the pixel data begins and there is no metadata beyond it.
      if (marker === 0xda) {
        return null;
      }

      offset += 2 + length;
    }

    return null;
  } catch {
    return null;
  }
}

function readExifDate(view: DataView, tiffStart: number): string | null {
  if (tiffStart + 8 > view.byteLength) {
    return null;
  }

  // The TIFF header declares its own endianness, and cameras genuinely ship both.
  const byteOrder = view.getUint16(tiffStart);
  const little = byteOrder === 0x4949;

  if (!little && byteOrder !== 0x4d4d) {
    return null;
  }

  const firstIfd = view.getUint32(tiffStart + 4, little);

  const exifIfd = findTag(view, tiffStart + firstIfd, 0x8769, little);

  if (exifIfd === null) {
    return null;
  }

  const original = readStringTag(view, tiffStart, tiffStart + exifIfd, 0x9003, little);

  if (!original) {
    return null;
  }

  // "yyyy:MM:dd HH:mm:ss", with no zone. OffsetTimeOriginal carries one when the camera bothered.
  const match = /^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2}):(\d{2})$/.exec(original.trim());

  if (!match) {
    return null;
  }

  const [, year, month, day, hour, minute, second] = match;
  const offset = readStringTag(view, tiffStart, tiffStart + exifIfd, 0x9011, little)?.trim();

  const zone = offset && /^[+-]\d{2}:\d{2}$/.test(offset) ? offset : 'Z';

  return `${year}-${month}-${day}T${hour}:${minute}:${second}${zone}`;
}

/**
 * The offset held by a LONG tag in one IFD, or null when it is not there.
 *
 * Unlike {@link readStringTag} this needs no TIFF origin: a LONG value is four bytes, so it always
 * lives inline in the entry rather than at a pointer that would have to be resolved against the
 * start of the TIFF block.
 */
function findTag(
  view: DataView,
  ifdStart: number,
  tag: number,
  little: boolean,
): number | null {
  if (ifdStart + 2 > view.byteLength) {
    return null;
  }

  const count = view.getUint16(ifdStart, little);

  for (let i = 0; i < count; i++) {
    const entry = ifdStart + 2 + i * 12;

    if (entry + 12 > view.byteLength) {
      return null;
    }

    if (view.getUint16(entry, little) === tag) {
      return view.getUint32(entry + 8, little);
    }
  }

  return null;
}

/** The value of an ASCII tag in one IFD. */
function readStringTag(
  view: DataView,
  tiffStart: number,
  ifdStart: number,
  tag: number,
  little: boolean,
): string | null {
  if (ifdStart + 2 > view.byteLength) {
    return null;
  }

  const entries = view.getUint16(ifdStart, little);

  for (let i = 0; i < entries; i++) {
    const entry = ifdStart + 2 + i * 12;

    if (entry + 12 > view.byteLength) {
      return null;
    }

    if (view.getUint16(entry, little) !== tag) {
      continue;
    }

    const length = view.getUint32(entry + 4, little);

    // Up to four bytes live inline; anything longer is a pointer. Both date tags are longer.
    const valueAt = length > 4 ? tiffStart + view.getUint32(entry + 8, little) : entry + 8;

    if (valueAt + length > view.byteLength) {
      return null;
    }

    return readAscii(view, valueAt, length).replace(/\0+$/, '');
  }

  return null;
}

function readAscii(view: DataView, at: number, length: number): string {
  let out = '';

  for (let i = 0; i < length; i++) {
    out += String.fromCharCode(view.getUint8(at + i));
  }

  return out;
}
